import { AfterViewInit, Component, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { CustomModalService } from '../../../shared-components/custom-modal/custom-modal.service';
import { CustomCheckboxComponent } from '../../../shared-components/custom-checkbox/custom-checkbox.component';
import { CustomInputComponent } from '../../../shared-components/custom-input/custom-input.component';
import { CustomSelectComponent, SelectOption } from '../../../shared-components/custom-select/custom-select.component';
import { TranslateService } from '../../../services/translate/translate.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { SmartFormManager, FormValidationUtils, ComponentType } from '../../../utils/form-validation';

interface FormModalValue {
	fullName: string;
	email: string;
	department: string;
	acceptTerms: boolean;
}

@Component({
	selector: 'app-modals-showcase',
	templateUrl: './modals-showcase.component.html',
	styleUrls: ['./modals-showcase.component.scss'],
	standalone: false,
})
export class ModalsShowcaseComponent implements AfterViewInit {
	@ViewChild('formModalTemplate') formModalTemplate!: TemplateRef<unknown>;
	@ViewChildren(CustomInputComponent) inputComponents!: QueryList<CustomInputComponent>;
	@ViewChildren(CustomSelectComponent) selectComponents!: QueryList<CustomSelectComponent>;
	@ViewChildren(CustomCheckboxComponent) checkboxComponents!: QueryList<CustomCheckboxComponent>;

	public isFormModalValid: boolean = false;
	public formManager: SmartFormManager | undefined;
	public departmentOptions: SelectOption[] = [
		{ label: 'Finance', value: 'finance' },
		{ label: 'Operations', value: 'operations' },
		{ label: 'Commercial', value: 'commercial' },
	];

	constructor(
		private modalService: CustomModalService,
		private translateService: TranslateService,
		private toasterService: ToasterService
	) {
		this.formManager = FormValidationUtils.createSmartFormManagerWithQueryList(this.formModalFieldConfigs, this.translateService);
	}

	ngAfterViewInit(): void {
		this.reinitializeFormModalManager();
	}

	showConfirmModal(): void {
		const modalRef = this.modalService.confirm({
			title: 'Confirm Action',
			tone: 'success',
			size: 'md',
			body: {
				content: 'This is a confirm modal that provides important details to the user. It uses the success tone to indicate its nature.',
			},
		});

		modalRef.result
			.then((result) => {
				console.log('Confirm result:', result);
			})
			.catch((reason) => {
				console.log('Confirm dismissed:', reason);
			});
	}

	showInfoModal(): void {
		this.modalService.alert({
			title: 'Information',
			tone: 'info',
			size: 'md',
			body: {
				content:
					'This is an informational modal that provides important details to the user. It uses the info tone to indicate its nature.',
			},
		});
	}

	showWarningModal(): void {
		this.modalService.alert({
			title: 'Warning',
			tone: 'warning',
			size: 'md',
			body: {
				content: 'This is a warning modal that provides important details to the user. It uses the warning tone to indicate its nature.',
			},
		});
	}

	showDangerModal(): void {
		this.modalService.confirm({
			title: 'Delete Item',
			tone: 'danger',
			size: 'md',
			body: {
				content: 'This is a danger modal that provides important details to the user. It uses the danger tone to indicate its nature.',
			},
		});
	}

	showSizeModal(size: 'md' | 'lg' | 'xl' | 'xxl'): void {
		switch (size) {
			case 'md':
				this.modalService.alert({
					title: 'Default Modal',
					tone: 'success',
					size: size,
					body: {
						content: this.generateContent(1),
					},
				});
				break;
			case 'lg':
				this.modalService.alert({
					title: 'Large Modal',
					tone: 'info',
					size: size,
					body: {
						content: this.generateContent(3),
					},
				});
				break;
			case 'xl':
				this.modalService.alert({
					title: 'Extra Large Modal',
					tone: 'warning',
					size: size,
					body: {
						content: this.generateContent(5),
					},
				});
				break;
			case 'xxl':
				this.modalService.alert({
					title: 'Double Extra Large Modal',
					tone: 'danger',
					size: size,
					body: {
						content: this.generateContent(10),
					},
				});
				break;
		}
	}

	showAsyncModal(): void {
		this.modalService.open({
			title: 'Async Action',
			tone: 'info',
			size: 'md',
			body: {
				kind: 'message',
				content: 'This modal demonstrates an async action with loading state. Click the button to simulate an API call.',
			},
			actions: [
				{
					id: 'cancel',
					label: 'Cancel',
					color: 'neutral',
					variant: 'outlined',
					dismissWith: 'cancelled',
				},
				{
					id: 'process',
					label: 'Process',
					color: 'primary',
					variant: 'standard',
					autoFocus: true,
					run: async () => {
						console.log('Processing...');
						await new Promise((resolve) => setTimeout(resolve, 2000));
						this.toasterService.showSuccess('Async operation completed', {
							description: 'The operation has been completed successfully.',
						});
					},
					closeWith: 'processed',
				},
			],
		});
	}

	showFormModal(): void {
		this.resetFormModal();

		const modalRef = this.modalService.open<FormModalValue>({
			title: 'User Form',
			tone: 'default',
			size: 'md',
			layout: 'form',
			body: {
				kind: 'template',
				templateRef: this.formModalTemplate,
			},
			actions: [
				{
					id: 'cancel',
					label: 'Cancel',
					color: 'neutral',
					variant: 'outlined',
					dismissWith: 'cancelled',
				},
				{
					id: 'save',
					label: 'Save',
					color: 'primary',
					variant: 'standard',
					run: async ({ modalRef }) => {
						if (!this.validateFormModal()) {
							return;
						}

						// Simulate async save operation
						console.log('Saving form data...');
						await new Promise((resolve) => setTimeout(resolve, 2000));
						this.toasterService.showSuccess('Modal form data has been saved', {
							description: 'Your changes have been saved successfully.',
						});

						modalRef.close(this.getFormModalResult());
					},
				},
			],
		});

		modalRef.afterOpened$.then(() => {
			setTimeout(() => {
				this.reinitializeFormModalManager();
			}, 0);
		});

		modalRef.afterClosed$.finally(() => {
			this.resetFormModal();
		});

		modalRef.result
			.then((result) => {
				console.log('Form submitted:', result);
			})
			.catch((reason) => {
				console.log('Form dismissed:', reason);
			});
	}

	dismissAllModals(): void {
		this.modalService.dismissAll();
	}

	getValidationRules(fieldName: keyof FormModalValue): any[] {
		return FormValidationUtils.getValidationRules(this.formManager?.formFields ?? {}, fieldName);
	}

	onFormModalValueChange(fieldName: keyof FormModalValue, value: string | boolean): void {
		if (this.formManager?.formFields?.[fieldName]) {
			this.formManager.formFields[fieldName].value = value;
		}
	}

	onFormModalValidationChange(fieldName: keyof FormModalValue, validation: { isValid: boolean }): void {
		if (this.formManager?.formFields?.[fieldName]) {
			this.formManager.formFields[fieldName].valid = validation.isValid;
			this.isFormModalValid = this.formManager.isValid();
		}
	}

	private generateContent(paragraphs: number): string {
		const paragraphsArray = [
			'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse id lectus nunc. In at dapibus tortor, tincidunt suscipit nibh. In maximus nunc at libero convallis mattis. Ut vitae congue magna. Nulla bibendum dapibus mollis. Nam sapien felis, sagittis ac mollis id, sagittis vel quam. Nunc egestas est at elit viverra, eget sollicitudin ipsum finibus. Vestibulum eu odio ex. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae; Duis vel lorem sed sem mollis accumsan sed ac tellus.',
			'Phasellus sodales placerat leo sed ultrices. Morbi vitae placerat ipsum, a eleifend justo. Morbi auctor lorem in enim rutrum, consequat finibus sem congue. Etiam imperdiet nisl vel purus condimentum tincidunt. Praesent rutrum euismod vulputate. Proin at mi mollis, pharetra mauris vel, tempus augue. Proin lobortis vehicula blandit. Vivamus faucibus turpis sit amet consectetur elementum. Cras eleifend lobortis dignissim. Donec eget risus commodo, aliquam quam ut, imperdiet leo. Nulla feugiat est quis ornare rutrum. Sed velit orci, scelerisque id augue ultricies, consectetur tempor odio.',
			'Pellentesque erat enim, semper sodales vestibulum a, volutpat vel ipsum. Nulla elementum efficitur justo, eu aliquam nisl fringilla sed. Suspendisse vulputate molestie dignissim. Donec sagittis id nisi eget faucibus. Morbi ac pulvinar turpis. In quis aliquam neque. Cras dictum mauris nec nulla vestibulum, quis auctor mauris tristique. Donec quis quam tempus, hendrerit tellus a, auctor est. Aenean bibendum magna et porttitor condimentum. Aliquam dapibus magna eget elementum pellentesque. Sed non erat ligula. Quisque euismod enim ut fermentum gravida. Phasellus euismod, orci id dictum elementum, mi diam dictum tortor, et scelerisque eros felis quis mauris. Morbi nec turpis at ante vehicula ullamcorper id ac sem. Mauris vulputate, libero non mattis pulvinar, est mi condimentum risus, ut consectetur magna ligula sed ex.',
			'Pellentesque ultrices ullamcorper arcu, ut iaculis dui. Donec eget diam tellus. Donec enim massa, aliquet sed urna congue, consequat tincidunt ipsum. Pellentesque at blandit quam. Maecenas viverra tellus elit, a vestibulum nunc bibendum scelerisque. Praesent sit amet odio in arcu congue luctus. Praesent elit nunc, vehicula eu congue quis, fermentum sit amet sapien.',
			'Interdum et malesuada fames ac ante ipsum primis in faucibus. Mauris quis tincidunt justo, ut consectetur lacus. Nulla metus nulla, malesuada eget rhoncus eu, interdum quis enim. Suspendisse ipsum diam, porttitor quis augue eu, aliquet faucibus magna. Nunc luctus augue ac ultrices hendrerit. Donec ligula sapien, tincidunt ultricies risus nec, convallis blandit elit. Morbi quis mi elementum, pharetra sapien quis, vestibulum metus. Donec nec dui a tellus cursus posuere. Nulla quis fringilla ex. Vestibulum in accumsan nulla, in luctus purus. Curabitur diam massa, ornare a leo vitae, pharetra egestas lacus. Nulla euismod tincidunt posuere. Integer dictum ut diam eu feugiat. Nam convallis iaculis pharetra. Aenean condimentum vehicula eleifend. Quisque orci felis, bibendum ac lectus vel, ullamcorper porta diam.',
			'Suspendisse eget lorem neque. Fusce vitae aliquam mauris. Suspendisse eleifend diam a rhoncus ornare. Duis ultrices sit amet mauris eu venenatis. Nam cursus ipsum sed velit tempor, eu aliquam lorem pulvinar. Aenean id lorem pharetra, porta urna vitae, scelerisque elit. Praesent tempor orci eu molestie finibus. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla in eleifend nisl.',
			'Maecenas dignissim lectus erat, sed lobortis justo blandit ut. Aenean imperdiet odio quis feugiat hendrerit. Morbi faucibus, tortor in lobortis rutrum, justo nibh vulputate neque, nec accumsan ex sapien quis nisl. Suspendisse aliquet porta iaculis. Curabitur a malesuada ligula. Nunc tincidunt erat non est rhoncus imperdiet. Donec tempus eros eros. Proin vel placerat turpis, nec tempor arcu. Nam maximus molestie blandit.',
			'Nullam fermentum, nunc non blandit pharetra, libero magna imperdiet mauris, in luctus neque metus ut turpis. Nulla et luctus eros. Nulla placerat neque tortor, eu elementum sapien pretium sit amet. Etiam mattis risus tincidunt, placerat ex pretium, rutrum nisi. Donec vulputate at tortor vitae viverra. Phasellus venenatis, diam quis ultricies varius, sapien purus tristique orci, feugiat elementum magna ex ac sem. Suspendisse eu quam eget diam fermentum venenatis. Integer pretium erat et consectetur vehicula.',
			'In at arcu semper, tincidunt metus sed, bibendum purus. Nunc malesuada dui ut velit bibendum, quis suscipit nunc ultrices. Ut varius sem et ipsum tristique, at suscipit nulla sagittis. Duis vitae maximus dolor. Nulla id leo facilisis, convallis nunc congue, iaculis neque. Quisque fermentum eget ipsum vitae mattis. Aliquam ligula ante, ultrices et eleifend eu, lacinia vel leo. Vestibulum venenatis nibh ut magna tincidunt commodo. In accumsan volutpat sagittis. Duis pretium leo in velit viverra, a elementum eros aliquam. Cras varius libero sed eros molestie gravida. Aenean ultrices pharetra lectus a auctor.',
			'Quisque vitae luctus arcu. Pellentesque at risus ut tellus malesuada consequat sed eu arcu. Phasellus sed viverra risus, ut ultricies sem. Sed malesuada libero ut dignissim ultrices. Integer est orci, sollicitudin eget sagittis sit amet, viverra vitae tellus. Etiam porttitor fringilla nibh in ullamcorper. In id gravida augue, vitae volutpat mi. Nunc sapien est, malesuada in elementum eget, aliquet vel nisi. Praesent dictum velit magna, semper venenatis neque laoreet vel. Donec vehicula facilisis porttitor. Vestibulum bibendum vel ex eu dignissim. Aliquam at metus sed nisl pulvinar lobortis iaculis in mauris.',
		];

		return paragraphsArray.slice(0, paragraphs).join('\n\n');
	}

	private validateFormModal(): boolean {
		if (!this.formManager) {
			return false;
		}

		this.formManager.validateAll();
		this.isFormModalValid = this.formManager.isValid();
		return this.isFormModalValid;
	}

	private reinitializeFormModalManager(): void {
		this.formManager = FormValidationUtils.createSmartFormManagerWithQueryList(
			this.formModalFieldConfigs,
			this.translateService,
			this.inputComponents,
			this.selectComponents,
			this.checkboxComponents
		);
		this.isFormModalValid = this.formManager.isValid();
	}

	private resetFormModal(): void {
		this.formManager = FormValidationUtils.createSmartFormManagerWithQueryList(this.formModalFieldConfigs, this.translateService);
		this.isFormModalValid = false;
	}

	private getFormModalResult(): FormModalValue {
		const values = this.formManager?.getValues() || {};

		return {
			fullName: values.fullName || '',
			email: values.email || '',
			department: values.department || '',
			acceptTerms: values.acceptTerms === true,
		};
	}

	private get formModalFieldConfigs() {
		const inputRules = CustomInputComponent.createRules(this.translateService);
		const selectRules = CustomSelectComponent.createRules(this.translateService);
		const checkboxRules = CustomCheckboxComponent.createRules(this.translateService);

		return [
			{ name: 'fullName', type: ComponentType.INPUT, validationRules: [inputRules.required()] },
			{ name: 'email', type: ComponentType.INPUT, validationRules: [inputRules.required(), inputRules.email()] },
			{ name: 'department', type: ComponentType.SELECT, validationRules: [selectRules.required()] },
			{ name: 'acceptTerms', type: ComponentType.CHECKBOX, validationRules: [checkboxRules.required()] },
		];
	}
}
